/* eslint-disable jsx-a11y/role-has-required-aria-props */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Image, ImageFit, Text } from '@fluentui/react';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';

import { getCampaignCreationMethodItemStyles } from '../styles/campaign-creation-method-item-styles';
import type {
  CampaignCreationMethodDefinition,
  CampaignCreationMethodValue,
  I18n,
} from './campaign-creation-method-constants';

export interface CampaignCreationMethodItemProps extends Omit<CampaignCreationMethodDefinition, 'forceContentInTwoLines'> {
  i18n: I18n;
  itemIndex: number;
  onMethodItemClick: (method: CampaignCreationMethodValue) => void;
  shouldForceContentInTwoLines?: boolean;
  isVertical?: boolean;
  selected?: boolean;
}

export function CampaignCreationMethodItem(props: CampaignCreationMethodItemProps) {
  const {
    i18n,
    itemIndex,
    onMethodItemClick,
    shouldForceContentInTwoLines,
    method,
    imageSrc,
    content,
    alt,
    label,
    isVertical,
    disabled,
    disableText,
    selected,
  } = props;

  const classes = useDefaultStyles(getCampaignCreationMethodItemStyles);

  const onClickItem = useCallback(() => {
    onMethodItemClick(method);
  }, [method, onMethodItemClick]);

  const itemProps = useMemo(() => {
    if (disabled) {
      return {
        'aria-disabled': true,
      };
    }
    return {
      'data-is-focusable': true,
      'data-selection-invoke': true,
      'data-selection-index': itemIndex,
      tabIndex: 0,
      role: 'option',
      onClick: onClickItem,
      onKeyDown: submitEvent(onClickItem),
    };
  }, [disabled, itemIndex, onClickItem]);

  return (
    <div
      className={
        classNames(
          'campaign-creation-method-selection-item',
          method,
          classes.itemContainer,
          isVertical && classes.verticalItemContainer,
          selected && classes.selectedItemContainer,
          disabled && classes.disabledItemContainer
        )
      }
      aria-label={i18n.getString(label)}
      title={i18n.getString(label)}
      aria-describedby={`${method}-platforms-describedby ${method}-comment-describedby`}
      {...itemProps}
    >
      <Image
        src={imageSrc}
        className={classNames(classes.imageContainerStyle, `${method}-image-container`, isVertical && classes.verticalImageContainer)}
        imageFit={ImageFit.contain}
        alt={i18n.getString(alt)}
      />
      <Text className={
        classNames(
          classes.content,
          { [classes.contentInTwoLines]: shouldForceContentInTwoLines }
        )
      }
      >
        {i18n.getString(content)}
      </Text>
      {disabled && disableText && isVertical &&
        <div className={classNames(classes.disableText, 'disable-text')}>
          {i18n.getString(disableText)}
        </div>
      }
    </div>
  );
}

CampaignCreationMethodItem.propTypes = {
  i18n: PropTypes.shape({ getString: PropTypes.func.isRequired }).isRequired,
  itemIndex: PropTypes.number.isRequired,
  onMethodItemClick: PropTypes.func.isRequired,

  method: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,

  shouldForceContentInTwoLines: PropTypes.bool,
  isVertical: PropTypes.bool,
  disabled: PropTypes.bool,
  disableText: PropTypes.string,
  selected: PropTypes.bool,
};

CampaignCreationMethodItem.defaultProps = {
  shouldForceContentInTwoLines: false,
  isVertical: false,
  disabled: false,
  disableText: '',
  selected: false,
};
